/**
 * W3C Trace Context helpers — client-side correlation for every API request.
 *
 * Provides:
 * - `getClientId()`: persistent browser identity (32 hex, localStorage).
 * - `getSessionId()`: per-tab identity (32 hex, sessionStorage).
 * - `newTraceId()` / `newSpanId()`: random hex ids for W3C traceparent.
 * - `buildTraceparent()` / `buildBaggage()` / `parseTraceresponse()`: header
 *   construction and parsing per the W3C Trace Context spec.
 *
 * Design: every function is try/catch-safe and never throws — storage can be
 * blocked (private browsing, full quota, iframe sandbox), and crypto may be
 * unavailable in insecure contexts. Fallback ids are valid hex, just not
 * persisted.
 */

const CLIENT_ID_KEY = 'ving.client_id';
const SESSION_ID_KEY = 'ving.session_id';

/**
 * `length` random hex chars, never all zero (W3C forbids an all-zero trace-id
 * or parent-id). Uses crypto.getRandomValues; when it is missing or throws,
 * falls back to Math.random. Never throws.
 */
function mintHex(length: number): string {
  for (;;) {
    let hex: string;
    try {
      const bytes = new Uint8Array(length / 2);
      crypto.getRandomValues(bytes);
      hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
    } catch {
      hex = mintHexFromMathRandom(length);
    }
    if (/[1-9a-f]/.test(hex)) return hex;
  }
}

function mintHexId(): string {
  return mintHex(32);
}

function mintShortHexId(): string {
  return mintHex(16);
}

/**
 * 32 random hex chars for a non-trace id (an Idempotency-Key, #1076-b). Inside the
 * browser floor (#981: not crypto.randomUUID). Never use it as a trace id (#1332).
 */
export function newRandomHexId(): string {
  return mintHex(32);
}

/** Per-key fallback cache: when storage throws, repeated calls return the
 *  same id for the same key so client/session identity stays stable. */
const _fallbackCache = new Map<string, string>();

/**
 * Read-or-mint a 32-hex id from the given storage. On ANY storage failure
 * (private browsing, quota, iframe sandbox) falls back to a per-load id that
 * is valid hex but not persisted — cached per key so repeated calls are
 * stable. Never throws.
 */
function getOrMintId(storage: Storage, key: string): string {
  try {
    const existing = storage.getItem(key);
    if (typeof existing === 'string' && /^[0-9a-f]{32}$/.test(existing)) {
      return existing;
    }
    const id = mintHexId();
    storage.setItem(key, id);
    return id;
  } catch {
    // Storage unavailable — return (or mint+cache) a per-load fallback.
    let cached = _fallbackCache.get(key);
    if (!cached) {
      cached = mintHexId();
      _fallbackCache.set(key, cached);
    }
    return cached;
  }
}

/**
 * Stable browser identity (32 hex). Persisted in `localStorage` key
 * `ving.client_id`. Minted once per browser; survives tab closes and
 * navigations. Falls back to a per-load id when storage is blocked.
 *
 * The outer try catches the case where accessing `localStorage` itself
 * throws (cross-origin iframes, some sandboxed contexts).
 */
export function getClientId(): string {
  try {
    return getOrMintId(localStorage, CLIENT_ID_KEY);
  } catch {
    let cached = _fallbackCache.get(CLIENT_ID_KEY);
    if (!cached) {
      cached = mintHexId();
      _fallbackCache.set(CLIENT_ID_KEY, cached);
    }
    return cached;
  }
}

/**
 * Per-tab identity (32 hex). Persisted in `sessionStorage` key
 * `ving.session_id`. Minted once per tab; cleared when the tab closes.
 * Falls back to a per-load id when storage is blocked.
 */
export function getSessionId(): string {
  try {
    return getOrMintId(sessionStorage, SESSION_ID_KEY);
  } catch {
    let cached = _fallbackCache.get(SESSION_ID_KEY);
    if (!cached) {
      cached = mintHexId();
      _fallbackCache.set(SESSION_ID_KEY, cached);
    }
    return cached;
  }
}

/**
 * Build a hex string of `length` chars from Math.random, for use when
 * crypto.getRandomValues is unavailable. Concatenates multiple random
 * slices to guarantee enough hex digits.
 */
function mintHexFromMathRandom(length: number): string {
  let hex = '';
  while (hex.length < length) {
    hex += Math.random().toString(16).slice(2);
  }
  return hex.slice(0, length);
}

/** 32 random hex chars — a W3C trace-id. Never all zero, never throws. */
export function newTraceId(): string {
  return mintHexId();
}

/** 16 random hex chars — a W3C span/parent-id. Never all zero, never throws. */
export function newSpanId(): string {
  return mintShortHexId();
}

/**
 * Build a W3C `traceparent` header value.
 * Format: `00-<32 hex trace-id>-<16 hex parent-id>-01`
 * Version 00, flags 01 (sampled).
 */
export function buildTraceparent(traceId: string, spanId: string): string {
  return `00-${traceId}-${spanId}-01`;
}

/**
 * Build a W3C `baggage` header value carrying client and session ids.
 */
export function buildBaggage(): string {
  return `client.id=${getClientId()},session.id=${getSessionId()}`;
}

/**
 * Parse a W3C `traceresponse` header value.
 * Expected format: `00-<32 hex trace-id>-<16 hex span-id>-<2 hex flags>`
 * Returns the trace-id and span-id, or undefined if the header is absent
 * or malformed.
 */
export function parseTraceresponse(
  header: string | null | undefined,
): { traceId: string; spanId: string } | undefined {
  if (typeof header !== 'string') return undefined;
  const match = /^00-([0-9a-f]{32})-([0-9a-f]{16})-[0-9a-f]{2}$/.exec(header);
  if (!match) return undefined;
  return { traceId: match[1]!, spanId: match[2]! };
}

/**
 * The trace-id of a request, read from the `traceparent` header the API client
 * stamped on it (#1104). A retry keeps it; undefined if the request has none.
 * Accepts a plain object or AxiosHeaders, any header-name case.
 */
export function requestTraceId(config: { headers?: unknown } | null | undefined): string | undefined {
  const headers = config?.headers as
    | { get?: (name: string) => unknown; [k: string]: unknown }
    | undefined;
  if (!headers) return undefined;
  let value: unknown = typeof headers.get === 'function' ? headers.get('traceparent') : undefined;
  if (typeof value !== 'string') {
    for (const key of Object.keys(headers)) {
      if (key.toLowerCase() === 'traceparent') {
        value = headers[key];
        break;
      }
    }
  }
  if (typeof value !== 'string') return undefined;
  const match = /^00-([0-9a-f]{32})-[0-9a-f]{16}-[0-9a-f]{2}$/.exec(value);
  return match ? match[1] : undefined;
}

/** The trace-id inside a raw `traceparent` header value; null when absent or malformed. For MSW handlers in tests. */
export function traceIdOfTraceparent(value: string | null | undefined): string | null {
  const match = typeof value === 'string' ? /^00-([0-9a-f]{32})-[0-9a-f]{16}-[0-9a-f]{2}$/.exec(value) : null;
  return match ? match[1]! : null;
}
